/**
 * Hero Banner Slideshow Component
 * Extends SlideshowComponent with:
 * - Fade transition mode
 * - Progress bar navigation
 * - Content entry animations
 * - Per-slide countdown timers
 * - Video pause/play management
 */
class HeroBannerSlideshowComponent extends SlideshowComponent {
  constructor() {
    super();

    // SliderComponent & SlideshowComponent return early when no navigation
    // buttons exist (1-slide scenario). Ensure essential properties are set.
    if (this.sliderItems && this.sliderItems.length > 0 && !this.currentPage) {
      this.currentPage = 1;
    }

    this.transitionType = this.dataset.transition || 'slide';
    this._countdownIntervals = [];
    this._progressFrame = null;

    if (this.transitionType === 'fade') {
      this.initFadeMode();
    }

    this.progressBar = this.querySelector('.hero-banner__progress-bar');
    if (this.progressBar && this.slider && this.slider.dataset.autoplay === 'true') {
      this.initProgressBar();
    }

    this.animateSlideContent(0);
    this.initCountdowns();
    this.manageSlideVideos();
    this.initStickyCta();
    this.initScheduling();
    this.initParallax();
    this.initSwipeHint();
    this.initVisibilityObserver();
    this.initVideoPoster();
    this.initQuoteRotation();
  }

  /* ==============================
   * FADE MODE
   * ============================== */

  initFadeMode() {
    if (!this.slider) return;
    this.slider.style.overflow = 'hidden';
    this.slider.style.scrollSnapType = 'none';
    this.slider.style.scrollBehavior = 'auto';

    const slides = this.slider.querySelectorAll('.slideshow__slide');
    slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === 0);
      slide.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
    });
  }

  fadeToSlide(targetPage) {
    const slides = this.slider.querySelectorAll('.slideshow__slide');
    if (!slides.length || targetPage < 1 || targetPage > slides.length) return;

    const prevPage = this.currentPage;
    if (prevPage === targetPage) return;

    slides.forEach((slide, i) => {
      const isTarget = i === targetPage - 1;
      slide.classList.toggle('is-active', isTarget);
      slide.setAttribute('aria-hidden', isTarget ? 'false' : 'true');

      const links = slide.querySelectorAll('a');
      links.forEach(link => {
        if (isTarget) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      });
    });

    this.currentPage = targetPage;

    if (this.currentPageElement) {
      this.currentPageElement.textContent = this.currentPage;
    }

    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    if (this.sliderControlButtons.length) {
      this.sliderControlButtons.forEach(link => {
        link.classList.remove('slider-counter__link--active');
        link.removeAttribute('aria-current');
      });
      if (this.sliderControlButtons[this.currentPage - 1]) {
        this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
        this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
      }
    }

    this.resetSlideContent(prevPage - 1);
    this.animateSlideContent(targetPage - 1);
    this.manageSlideVideos();

    if (this.progressBar) this.resetProgressBar();

    this.dispatchEvent(new CustomEvent('slideChanged', {
      detail: { currentPage: this.currentPage, currentElement: slides[this.currentPage - 1] }
    }));
  }

  /* ==============================
   * OVERRIDE: autoRotateSlides
   * ============================== */

  autoRotateSlides() {
    if (this.transitionType === 'fade') {
      const target = this.currentPage >= this.sliderItems.length ? 1 : this.currentPage + 1;
      this.fadeToSlide(target);
    } else {
      super.autoRotateSlides();
    }
  }

  /* ==============================
   * OVERRIDE: onButtonClick
   * ============================== */

  onButtonClick(event) {
    if (this.transitionType === 'fade') {
      event.preventDefault();
      const isNext = event.currentTarget.name === 'next';
      let target;
      if (isNext) {
        target = this.currentPage >= this.sliderItems.length ? 1 : this.currentPage + 1;
      } else {
        target = this.currentPage <= 1 ? this.sliderItems.length : this.currentPage - 1;
      }
      this.fadeToSlide(target);
      if (this.autoplayButtonIsSetToPlay) this.play();
    } else {
      super.onButtonClick(event);
    }
  }

  /* ==============================
   * OVERRIDE: linkToSlide
   * ============================== */

  linkToSlide(event) {
    if (this.transitionType === 'fade') {
      event.preventDefault();
      const targetPage = this.sliderControlLinksArray.indexOf(event.currentTarget) + 1;
      this.fadeToSlide(targetPage);
      if (this.autoplayButtonIsSetToPlay) this.play();
    } else {
      super.linkToSlide(event);
    }
  }

  /* ==============================
   * OVERRIDE: update (scroll-based, for slide mode)
   * ============================== */

  update() {
    const previousPage = this.currentPage;
    super.update();
    if (this.currentPage !== previousPage && this.transitionType === 'slide') {
      this.resetSlideContent(previousPage - 1);
      this.animateSlideContent(this.currentPage - 1);
      this.manageSlideVideos();
      if (this.progressBar) this.resetProgressBar();
    }
  }

  /* ==============================
   * CONTENT ENTRY ANIMATIONS
   * ============================== */

  animateSlideContent(slideIndex) {
    const slide = this.sliderItems[slideIndex];
    if (!slide) return;
    const content = slide.querySelector('.hero-slide__content--animate');
    if (content) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          content.classList.add('hero-slide__content--visible');
        });
      });
    }
  }

  resetSlideContent(slideIndex) {
    const slide = this.sliderItems[slideIndex];
    if (!slide) return;
    const content = slide.querySelector('.hero-slide__content--animate');
    if (content) {
      content.classList.remove('hero-slide__content--visible');
    }
  }

  /* ==============================
   * PROGRESS BAR
   * ============================== */

  initProgressBar() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this._progressStart = null;
    this.runProgressBar();
  }

  runProgressBar() {
    if (!this.progressBar) return;
    this._progressStart = Date.now();
    this._progressFrame = requestAnimationFrame(this.tickProgress.bind(this));
  }

  tickProgress() {
    if (!this.progressBar || !this._progressStart) return;
    const elapsed = Date.now() - this._progressStart;
    const pct = Math.min(elapsed / this.autoplaySpeed * 100, 100);
    this.progressBar.style.width = pct + '%';
    if (pct < 100) {
      this._progressFrame = requestAnimationFrame(this.tickProgress.bind(this));
    }
  }

  resetProgressBar() {
    if (!this.progressBar) return;
    cancelAnimationFrame(this._progressFrame);
    this.progressBar.style.transition = 'none';
    this.progressBar.style.width = '0%';
    void this.progressBar.offsetWidth;
    this.progressBar.style.transition = '';
    if (this.autoplayButtonIsSetToPlay) {
      this.runProgressBar();
    }
  }

  /* ==============================
   * OVERRIDE: play / pause (progress bar sync)
   * ============================== */

  play() {
    super.play();
    if (this.progressBar) this.runProgressBar();
  }

  pause() {
    super.pause();
    if (this.progressBar) {
      cancelAnimationFrame(this._progressFrame);
    }
  }

  /* ==============================
   * VIDEO MANAGEMENT
   * ============================== */

  manageSlideVideos() {
    if (!this.sliderItems) return;
    this.sliderItems.forEach((slide, i) => {
      const videos = slide.querySelectorAll('video');
      if (i === this.currentPage - 1) {
        videos.forEach(v => v.play().catch(() => {}));
      } else {
        videos.forEach(v => v.pause());
      }
    });
  }

  /* ==============================
   * COUNTDOWN TIMERS
   * ============================== */

  initCountdowns() {
    this.querySelectorAll('[data-countdown-end]').forEach(el => {
      const end = new Date(el.dataset.countdownEnd).getTime();
      if (isNaN(end)) return;

      const daysEl = el.querySelector('[data-days]');
      const hoursEl = el.querySelector('[data-hours]');
      const minEl = el.querySelector('[data-minutes]');
      const secEl = el.querySelector('[data-seconds]');

      function tick() {
        const diff = end - Date.now();
        if (diff <= 0) {
          el.style.display = 'none';
          return;
        }
        el.style.display = '';
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        if (daysEl) daysEl.textContent = String(d).padStart(2, '0');
        if (hoursEl) hoursEl.textContent = String(h).padStart(2, '0');
        if (minEl) minEl.textContent = String(m).padStart(2, '0');
        if (secEl) secEl.textContent = String(s).padStart(2, '0');
      }

      tick();
      this._countdownIntervals.push(setInterval(tick, 1000));
    });
  }

  /* ==============================
   * STICKY MOBILE CTA
   * ============================== */

  initStickyCta() {
    this._stickyCta = this.querySelector('.hero-slide__sticky-cta');
    if (!this._stickyCta) return;

    this._stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this._stickyCta.classList.remove('is-visible');
        } else {
          this._stickyCta.classList.add('is-visible');
        }
      });
    }, { threshold: 0.1 });

    this._stickyObserver.observe(this);
  }

  /* ==============================
   * SLIDE SCHEDULING
   * ============================== */

  initScheduling() {
    const now = Date.now();
    let anyHidden = false;

    this.querySelectorAll('.slideshow__slide[data-schedule-start], .slideshow__slide[data-schedule-end]').forEach(slide => {
      const startStr = slide.dataset.scheduleStart;
      const endStr = slide.dataset.scheduleEnd;
      let visible = true;

      if (startStr) {
        const start = new Date(startStr + 'T00:00').getTime();
        if (!isNaN(start) && now < start) visible = false;
      }
      if (endStr) {
        const end = new Date(endStr + 'T23:59:59').getTime();
        if (!isNaN(end) && now > end) visible = false;
      }

      if (!visible) {
        slide.classList.add('slideshow__slide--scheduled-hidden');
        anyHidden = true;
      }
    });

    if (anyHidden && this.slider) {
      this.resetPages();
      if (this.sliderItemsToShow.length <= 1) {
        const controls = this.querySelectorAll('.slideshow__controls');
        controls.forEach(c => c.style.display = 'none');
      }
    }
  }

  /* ==============================
   * PARALLAX
   * ============================== */

  initParallax() {
    if (!this.classList.contains('hero-banner--parallax')) return;
    this._parallaxBound = this.handleParallax.bind(this);
    window.addEventListener('scroll', this._parallaxBound, { passive: true });
    this.handleParallax();
  }

  handleParallax() {
    const rect = this.getBoundingClientRect();
    const windowH = window.innerHeight;
    if (rect.bottom < 0 || rect.top > windowH) return;
    const progress = (windowH - rect.top) / (windowH + rect.height);
    const offset = (progress - 0.5) * 40;
    const activeSlide = this.sliderItems[this.currentPage - 1];
    if (activeSlide) {
      const media = activeSlide.querySelector('.slideshow__media');
      if (media) media.style.transform = `translateY(${offset}px)`;
    }
  }

  /* ==============================
   * SWIPE HINT (MOBILE)
   * ============================== */

  initSwipeHint() {
    this._swipeHint = this.querySelector('.hero-banner__swipe-hint');
    if (!this._swipeHint) return;
    if (!this.sliderItems || this.sliderItems.length <= 1) {
      this._swipeHint.style.display = 'none';
      return;
    }

    try {
      if (sessionStorage.getItem('hero-swipe-seen')) {
        this._swipeHint.classList.add('is-hidden');
        return;
      }
    } catch (e) {}

    setTimeout(() => {
      this._swipeHint.classList.add('is-hidden');
      try { sessionStorage.setItem('hero-swipe-seen', '1'); } catch (e) {}
    }, 5000);
  }

  /* ==============================
   * VISIBILITY OBSERVER (pause when off-screen)
   * ============================== */

  initVisibilityObserver() {
    if (!this.slider || this.slider.dataset.autoplay !== 'true') return;

    this._visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (this._wasPausedByVisibility && this.autoplayButtonIsSetToPlay) {
            this.play();
            this._wasPausedByVisibility = false;
          }
        } else {
          if (this.autoplayButtonIsSetToPlay) {
            this.pause();
            this._wasPausedByVisibility = true;
          }
        }
      });
    }, { threshold: 0.1 });

    this._visibilityObserver.observe(this);
  }

  /* ==============================
   * VIDEO POSTER
   * ============================== */

  initVideoPoster() {
    this.querySelectorAll('.hero-banner__video-poster').forEach(poster => {
      const video = poster.parentElement.querySelector('video');
      if (!video) return;
      const hidePoster = () => {
        poster.classList.add('is-hidden');
        video.removeEventListener('playing', hidePoster);
      };
      video.addEventListener('playing', hidePoster);
      if (!video.paused) hidePoster();
    });
  }

  /* ==============================
   * QUOTE ROTATION
   * ============================== */

  initQuoteRotation() {
    this._quoteIntervals = [];
    this.querySelectorAll('.hero-banner__review-quotes--rotate').forEach(container => {
      const quotes = container.querySelectorAll('.hero-banner__review-quote');
      if (quotes.length <= 1) return;

      /* Measure tallest quote and lock container height to prevent layout shift */
      let maxHeight = 0;
      quotes.forEach(q => {
        q.style.position = 'relative';
        q.style.opacity = '0';
        q.style.display = 'block';
        const h = q.scrollHeight;
        if (h > maxHeight) maxHeight = h;
        q.style.position = '';
        q.style.opacity = '';
        q.style.display = '';
      });
      if (maxHeight > 0) container.style.minHeight = maxHeight + 'px';

      const speed = (parseInt(container.dataset.rotateSpeed, 10) || 6) * 1000;
      let current = 0;

      const rotate = () => {
        quotes[current].classList.remove('is-active');
        quotes[current].setAttribute('aria-hidden', 'true');
        current = (current + 1) % quotes.length;
        quotes[current].classList.add('is-active');
        quotes[current].removeAttribute('aria-hidden');
      };

      this._quoteIntervals.push(setInterval(rotate, speed));
    });
  }

  /* ==============================
   * CLEANUP
   * ============================== */

  disconnectedCallback() {
    super.disconnectedCallback();
    this._countdownIntervals.forEach(id => clearInterval(id));
    this._countdownIntervals = [];
    if (this._quoteIntervals) {
      this._quoteIntervals.forEach(id => clearInterval(id));
      this._quoteIntervals = [];
    }
    if (this._progressFrame) {
      cancelAnimationFrame(this._progressFrame);
    }
    if (this._stickyObserver) {
      this._stickyObserver.disconnect();
    }
    if (this._parallaxBound) {
      window.removeEventListener('scroll', this._parallaxBound);
    }
    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
    }
  }
}

customElements.define('hero-banner-slideshow', HeroBannerSlideshowComponent);
